/** @file ClipMetaForm: edits a clip's timing, loop, mirrorHalf and facing; Save sends one clip.setMeta. */
import type { ClipInputs } from '@zilo/pose3d-commands/types/clipInputs';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { ReactElement } from 'react';
import { useState } from 'react';

/** The fields `clip.setMeta` merges. */
type MetaFields = ClipInputs['clip.setMeta']['fields'];

/** The clip and what Save (with only the changed fields) and Cancel do. */
export type ClipMetaFormProps = { clip: Clip; onSave: (fields: MetaFields) => void; onCancel: () => void };

const num = { width: 48, font: 'inherit' };

/** `facingSteps` reads `free`, an integer, or empty (keeps the current value). */
const facingOf = (text: string): number | 'free' | undefined => {
  if (text === 'free') return 'free';
  return /^\d+$/.test(text) ? Number(text) : undefined;
};

/** Number fields fps / frames / loopStart, checkboxes loop / mirrorHalf, a facing field. */
export function ClipMetaForm({ clip, onSave, onCancel }: ClipMetaFormProps): ReactElement {
  const [d, setD] = useState({ fps: clip.fps, frames: clip.frames, loopStart: clip.loopStart, loop: clip.loop, mirrorHalf: clip.mirrorHalf, facing: String(clip.facingSteps ?? '') });
  const save = (): void => {
    const facing = facingOf(d.facing);
    const all: MetaFields = { fps: d.fps, frames: d.frames, loopStart: d.loopStart, loop: d.loop, mirrorHalf: d.mirrorHalf, ...(facing === undefined ? {} : { facingSteps: facing }) };
    const changed = Object.fromEntries(Object.entries(all).filter(([k, v]) => clip[k as keyof MetaFields] !== v)) as MetaFields;
    if (Object.keys(changed).length > 0) onSave(changed);
    else onCancel();
  };
  const n = (k: 'fps' | 'frames' | 'loopStart') => (
    <label>{k} <input type="number" aria-label={k} style={num} value={d[k]} onChange={(e) => { setD({ ...d, [k]: e.currentTarget.valueAsNumber }); }} /></label>
  );
  return (
    <form aria-label="clip meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: 4, fontSize: 11 }} onSubmit={(e) => { e.preventDefault(); save(); }}>
      {n('fps')}{n('frames')}{n('loopStart')}
      <label><input type="checkbox" aria-label="loop" checked={d.loop} onChange={(e) => { setD({ ...d, loop: e.currentTarget.checked }); }} /> loop</label>
      <label><input type="checkbox" aria-label="mirrorHalf" checked={d.mirrorHalf} onChange={(e) => { setD({ ...d, mirrorHalf: e.currentTarget.checked }); }} /> mirror half</label>
      <label>facing <input aria-label="facingSteps" style={num} placeholder="puppet" value={d.facing} onChange={(e) => { setD({ ...d, facing: e.currentTarget.value }); }} /></label>
      <button type="submit">Save</button>
      <button type="button" onClick={onCancel}>Cancel</button>
    </form>
  );
}
